/**
 * SegmentMapPreview — lightweight Leaflet map showing the selected segment's
 * start/end points and any inline geometry points (if returned by the API).
 *
 * Falls back to text coordinates if geometry data is missing.
 */

import { useMemo } from 'react';
import { MapPin } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Polyline, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { MovementSegment } from '../../../types/vehicleMovementTimeline';
import { CHART_PALETTE, STATUS_COLORS } from '../../../config/colors';

// Fix Leaflet default marker icons
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
const DefaultIcon = L.icon({ iconUrl: icon, shadowUrl: iconShadow, iconSize: [25, 41], iconAnchor: [12, 41] });
L.Marker.prototype.options.icon = DefaultIcon;

interface SegmentMapPreviewProps {
  segment: MovementSegment;
}

export default function SegmentMapPreview({ segment }: SegmentMapPreviewProps) {
  const geo = segment.geometry;

  // If no geometry at all, show nothing
  if (!geo || (!geo.start_point && !geo.end_point && !geo.points?.length)) {
    return (
      <div className="bg-card rounded-lg border border-border p-4 text-center text-muted-foreground text-xs">
        <MapPin className="w-5 h-5 mx-auto mb-1 opacity-40" />
        No geometry data for this segment
      </div>
    );
  }

  // Gather all points for bounds calculation
  const allPoints = useMemo(() => {
    const pts: [number, number][] = [];
    if (geo.start_point && geo.start_point.lat !== undefined && geo.start_point.lon !== undefined) {
      pts.push([geo.start_point.lat, geo.start_point.lon]);
    }
    if (geo.points) {
      for (const p of geo.points) {
        if (p.lat !== undefined && p.lon !== undefined) {
          pts.push([p.lat, p.lon]);
        }
      }
    }
    if (geo.end_point && geo.end_point.lat !== undefined && geo.end_point.lon !== undefined) {
      pts.push([geo.end_point.lat, geo.end_point.lon]);
    }
    return pts;
  }, [geo]);

  // If Leaflet loaded, render map
  const center: [number, number] = allPoints.length > 0
    ? [
        allPoints.reduce((s, p) => s + p[0], 0) / allPoints.length,
        allPoints.reduce((s, p) => s + p[1], 0) / allPoints.length,
      ]
    : [51.5, -0.1];

  // Build bounds for fitBounds
  const bounds = allPoints.length >= 2
    ? L.latLngBounds(allPoints.map(([lat, lng]: [number, number]) => L.latLng(lat, lng)))
    : undefined;

  const polylineColor =
    segment.type === 'MOVING'
      ? STATUS_COLORS.success
      : segment.type === 'STOPPED'
        ? STATUS_COLORS.warning
        : CHART_PALETTE[7];

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="px-4 py-2 border-b border-border">
        <h3 className="text-sm font-medium text-foreground flex items-center gap-1">
          <MapPin className="w-3.5 h-3.5" /> Segment Map
        </h3>
      </div>
      <div className="h-56">
        <MapContainer
          center={center}
          zoom={13}
          bounds={bounds}
          boundsOptions={{ padding: [30, 30] }}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={false}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          />
          {/* Polyline for all points */}
          {allPoints.length >= 2 && (
            <Polyline positions={allPoints} color={polylineColor} weight={3} />
          )}
          {/* Start marker */}
          {geo.start_point && geo.start_point.lat !== undefined && geo.start_point.lon !== undefined && (
            <Marker position={[geo.start_point.lat, geo.start_point.lon]}>
              <Popup>
                <span className="text-xs">Start: {geo.start_point.lat.toFixed(5)}, {geo.start_point.lon.toFixed(5)}</span>
              </Popup>
            </Marker>
          )}
          {/* End marker */}
          {geo.end_point && geo.end_point.lat !== undefined && geo.end_point.lon !== undefined && (
            <Marker position={[geo.end_point.lat, geo.end_point.lon]}>
              <Popup>
                <span className="text-xs">End: {geo.end_point.lat.toFixed(5)}, {geo.end_point.lon.toFixed(5)}</span>
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </div>
    </div>
  );
}
